// Power BI-like semantic palette for status badges.
// Returns class strings + dot colors. Designed to be shared globally.

export const TONES = {
  success:    { bg: "bg-emerald-50",  text: "text-emerald-800",  border: "border-emerald-300",  dot: "bg-emerald-600" },
  active:     { bg: "bg-blue-50",     text: "text-blue-800",     border: "border-blue-300",     dot: "bg-blue-600" },
  info:       { bg: "bg-slate-100",   text: "text-slate-700",    border: "border-slate-300",    dot: "bg-slate-500" },
  warning:    { bg: "bg-amber-50",    text: "text-amber-800",    border: "border-amber-300",    dot: "bg-amber-500" },
  risk:       { bg: "bg-orange-50",   text: "text-orange-800",   border: "border-orange-300",   dot: "bg-orange-500" },
  critical:   { bg: "bg-rose-50",     text: "text-rose-800",     border: "border-rose-300",     dot: "bg-rose-600" },
  finance:    { bg: "bg-indigo-50",   text: "text-indigo-800",   border: "border-indigo-300",   dot: "bg-indigo-600" },
  compliance: { bg: "bg-violet-50",   text: "text-violet-800",   border: "border-violet-300",   dot: "bg-violet-600" },
  submission: { bg: "bg-teal-50",     text: "text-teal-800",     border: "border-teal-300",     dot: "bg-teal-700" },
  neutral:    { bg: "bg-slate-100",   text: "text-slate-700",    border: "border-slate-200",    dot: "bg-slate-400" },
};

// status-by-type maps. Keys are normalized UPPER_SNAKE.
const STATUS_MAP = {
  case: {
    CREATED: "info", ACTIVE: "active", IN_PROGRESS: "active",
    BLOCKED: "critical", ON_HOLD: "warning", BLOCKED_DEPENDENCY: "risk",
    FINALIZED: "success", CLOSED: "success", CANCELLED: "critical", REOPENED: "warning",
  },
  health: {
    HEALTHY: "success", NORMAL: "info", WARNING: "warning",
    AT_RISK: "risk", CRITICAL: "critical",
  },
  payment: {
    NOT_INVOICED: "neutral", INVOICED: "finance",
    PARTIALLY_PAID: "warning", PAID: "success",
    PAYMENT_BLOCKED: "critical", OVERDUE: "critical",
    UNALLOCATED: "warning", PARTIALLY_ALLOCATED: "warning", ALLOCATED: "success",
  },
  invoice: {
    DRAFT: "neutral", ISSUED: "finance",
    PARTIALLY_PAID: "warning", PAID: "success", OVERDUE: "critical",
    CREDITED_PARTIAL: "finance", CREDITED_FULL: "neutral", CANCELLED: "critical",
  },
  quotation: {
    DRAFT: "neutral", INTERNAL_REVIEW: "compliance",
    SENT: "active", ACCEPTED: "success", REJECTED: "critical",
    EXPIRED: "warning", REVISED: "info", APPROVAL_REQUIRED: "warning",
    CONVERTED_TO_CASE: "success",
  },
  lead: {
    NEW: "active", CONTACTED: "info", QUALIFIED: "success",
    QUOTED: "finance", WON: "success", LOST: "critical",
    CONVERTED: "success", DUPLICATE_SUSPECTED: "warning",
  },
  document: {
    REQUIRED: "warning", REQUESTED: "active",
    UPLOADED: "info", UNDER_REVIEW: "compliance",
    APPROVED: "success", REJECTED: "critical",
    WAIVED: "warning", NOT_APPLICABLE: "neutral",
    EXPIRED: "critical", SUPERSEDED: "neutral",
    VIRUS_SCAN_FAILED: "critical", UPLOAD_FAILED: "critical",
  },
  submission: {
    PACKAGE_DRAFT: "neutral", INTERNAL_APPROVAL_PENDING: "compliance",
    SUBMITTED: "active", AWAITING_RESPONSE: "warning",
    CLARIFICATION_OPEN: "warning", RESUBMITTED: "active",
    PARTIALLY_APPROVED: "warning", ADDITIONAL_FEE_REQUIRED: "finance",
    WITHDRAWN: "neutral", REJECTED: "critical", APPROVED: "success",
  },
  renewal: {
    OPEN: "active", QUALIFIED: "success", DEFERRED: "warning",
    LOST: "critical", CONVERTED_TO_QUOTATION: "info",
    CONVERTED_TO_CASE: "success", EXPIRED: "critical",
    SURVEILLANCE_DUE: "warning",
  },
  user: {
    INVITED: "info", ACTIVE: "success", INACTIVE: "neutral",
    SUSPENDED: "critical", LOCKED: "critical",
    PASSWORD_RESET_REQUIRED: "warning",
  },
  // Run 2 — master-data lifecycle statuses (products; extends to other masters in Run 3).
  lifecycle: {
    ACTIVE: "success",
    INACTIVE: "neutral",
    END_OF_LIFE: "warning",
    ARCHIVED: "info",
  },
  priority: {
    LOW: "neutral", NORMAL: "info", HIGH: "warning", URGENT: "critical", CRITICAL: "critical",
  },
};

// Generic fallback when type isn't recognised — scans verb roots
function genericTone(s) {
  if (!s) return "neutral";
  if (/(approv|paid|won|complete|finaliz|active|healthy|success|delivered|allocated|qualified|invoiced$)/i.test(s)) return "success";
  if (/(submit|in_progress|pending|process|onboarding|investig|review|sent|contacted|resubmit)/i.test(s)) return "active";
  if (/(warn|partial|on_hold|onhold|deferred|expired|revised|password_reset|awaiting|clarif)/i.test(s)) return "warning";
  if (/(risk|delay|degrad|surveillance|negotiat|additional_fee)/i.test(s)) return "risk";
  if (/(block|breach|critical|fail|reject|cancel|suspended|locked|lost|churn|virus|overdue)/i.test(s)) return "critical";
  if (/(invoice|invoiced|billing|finance|credited|issued|quoted)/i.test(s)) return "finance";
  if (/(compl|review|audit)/i.test(s)) return "compliance";
  return "neutral";
}

/**
 * Resolve a Power BI-like tone for a given status + type.
 * - type: "case" | "health" | "payment" | "invoice" | "quotation" | "lead"
 *         | "document" | "submission" | "renewal" | "user" | "priority" | "generic"
 * - status: any string. Normalised internally.
 */
export function getStatusTone(status, type = "generic") {
  if (!status) return TONES.neutral;
  const norm = String(status).trim().toUpperCase().replace(/[\s-]+/g, "_");
  const map = STATUS_MAP[type] || {};
  const key = map[norm] || genericTone(norm);
  return TONES[key] || TONES.neutral;
}
